import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";

const STATUS_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Pending Renewal", value: "pending_renewal" },
    { label: "Expired", value: "expired" },
];

const PAYMENT_FREQUENCIES = [
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Semi Annual", value: "semi_annual" },
    { label: "Annual", value: "annual" },
    { label: "One-time", value: "one_time" },
];

const defaultFormState = {
    policy_number: "",
    customer_id: "",
    policy_holder_name: "",
    coverage_type: "",
    coverage_amount: "",
    premium_amount: "",
    payment_frequency: "monthly",
    product_id: "",
    nominee_name: "",
    nominee_relationship: "",
    start_date: "",
    end_date: "",
    status: "active",
    notes: "",
};

const EditPolicy = () => {
    const { userData } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();

    const policyId = useMemo(() => Number(id), [id]);

    const [formData, setFormData] = useState(defaultFormState);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [coverageSuggestions, setCoverageSuggestions] = useState([]);
    const [contextInfo, setContextInfo] = useState({ companyId: null, agentId: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!policyId) {
            toast.error("Invalid policy reference");
            navigate("/insurance/policies", { replace: true });
            return;
        }
        loadPolicy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyId, userData?.id]);

    const loadPolicy = async () => {
        setLoading(true);
        try {
            const policy = await fetchPolicy(policyId);
            if (!policy) {
                return;
            }

            const companyId =
                policy.company_id ||
                userData?.company_id ||
                userData?.companyId ||
                userData?.company ||
                null;
            const agentId = policy.agent_id || userData?.agent_id || userData?.agentId || null;

            setContextInfo({ companyId, agentId });

            setFormData({
                policy_number: policy.policy_number || "",
                customer_id: policy.customer_id ? String(policy.customer_id) : "",
                policy_holder_name: policy.policy_holder_name || "",
                coverage_type: policy.coverage_type || "",
                coverage_amount: policy.coverage_amount ? String(policy.coverage_amount) : "",
                premium_amount: policy.premium_amount ? String(policy.premium_amount) : "",
                payment_frequency: policy.payment_frequency || "monthly",
                product_id: policy.product_id ? String(policy.product_id) : "",
                nominee_name: policy.nominee_name || "",
                nominee_relationship: policy.nominee_relationship || "",
                start_date: policy.start_date ? policy.start_date.slice(0, 10) : "",
                end_date: policy.end_date ? policy.end_date.slice(0, 10) : "",
                status: policy.status || "active",
                notes: policy.notes || "",
            });

            if (companyId) {
                await fetchReferenceData(companyId);
            } else {
                setCustomers([]);
                setProducts([]);
            }
        } catch (error) {
            console.error("Error loading policy details:", error);
            toast.error("Unable to load policy");
            navigate("/insurance/policies", { replace: true });
        } finally {
            setLoading(false);
        }
    };

    const fetchPolicy = async (policyIdToFetch) => {
        const baseConditions = { id: policyIdToFetch };
        const policyResponse = await oneKlicks.read(
            "",
            "insurance_policies",
            baseConditions,
            "id DESC",
            "",
            "",
            "1"
        );

        if (policyResponse?.success && Array.isArray(policyResponse?.data) && policyResponse.data.length) {
            return policyResponse.data[0];
        }

        toast.error("Policy not found");
        navigate("/insurance/policies", { replace: true });
        return null;
    };

    const fetchReferenceData = async (companyId) => {
        try {
            const customerRes = await oneKlicks.read(
                "",
                "insurance_customers",
                { company_id: companyId },
                "name ASC",
                "",
                "",
                "0,250"
            );

            if (customerRes?.success) {
                const rows = Array.isArray(customerRes?.data)
                    ? customerRes.data
                    : Array.isArray(customerRes?.data?.items)
                        ? customerRes.data.items
                        : [];
                setCustomers(rows);
            } else {
                setCustomers([]);
            }

            const productRes = await oneKlicks.read(
                "",
                "insurance_products",
                { company_id: companyId },
                "name ASC",
                "",
                "",
                "0,100"
            );

            if (productRes?.success && Array.isArray(productRes?.data)) {
                setProducts(productRes.data);
            } else {
                setProducts([]);
            }

            const coverageRes = await oneKlicks.read(
                "",
                "insurance_policies",
                { company_id: companyId },
                "id DESC",
                "",
                "",
                "0,150"
            );
            if (coverageRes?.success && Array.isArray(coverageRes?.data)) {
                const options = Array.from(
                    new Set(
                        coverageRes.data
                            .map((row) => row.coverage_type)
                            .filter((value) => typeof value === "string" && value.trim().length)
                    )
                );
                setCoverageSuggestions(options);
            }
        } catch (error) {
            console.error("Error fetching reference data:", error);
            toast.error("Unable to load supporting data");
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectCustomer = (event) => {
        const selectedId = event.target.value;
        const selectedCustomer = customers.find((cust) => String(cust.id) === selectedId);
        setFormData((prev) => ({
            ...prev,
            customer_id: selectedId,
            policy_holder_name:
                selectedCustomer?.name ||
                selectedCustomer?.full_name ||
                selectedCustomer?.display_name ||
                prev.policy_holder_name,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formData.policy_number || !formData.policy_holder_name || !formData.coverage_type) {
            toast.error("Please fill in all required fields");
            return;
        }
        if (!formData.customer_id) {
            toast.error("Customer selection is required");
            return;
        }
        const { companyId, agentId } = contextInfo;
        if (!companyId) {
            toast.error("Missing company context");
            return;
        }

        setSaving(true);
        try {
            const updatedPayload = {
                policy_number: formData.policy_number,
                customer_id: Number(formData.customer_id),
                policy_holder_name: formData.policy_holder_name,
                coverage_type: formData.coverage_type,
                coverage_amount: formData.coverage_amount ? Number(formData.coverage_amount) : 0,
                premium_amount: formData.premium_amount ? Number(formData.premium_amount) : 0,
                payment_frequency: formData.payment_frequency,
                product_id: formData.product_id ? Number(formData.product_id) : null,
                nominee_name: formData.nominee_name || null,
                nominee_relationship: formData.nominee_relationship || null,
                start_date: formData.start_date || null,
                end_date: formData.end_date || null,
                status: formData.status,
                notes: formData.notes || null,
                company_id: companyId,
                agent_id: agentId || null,
                updated_at: new Date().toISOString().slice(0, 19).replace("T", " "),
            };

            const response = await oneKlicks.update(
                "",
                "insurance_policies",
                updatedPayload,
                { id: policyId }
            );

            if (response?.success) {
                toast.success("Policy updated successfully");
                navigate("/insurance/policies");
            } else {
                toast.error(response?.message || response?.error || "Unable to update policy");
            }
        } catch (error) {
            console.error("Error updating policy:", error);
            toast.error("Failed to update policy");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "24px" }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: "1080px", margin: "0 auto" }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <h2 style={{ marginBottom: 0 }}>Edit Policy</h2>
                            <p style={{ marginBottom: 0, color: "#64748b", fontSize: "14px" }}>
                                Update policy details and save changes instantly.
                            </p>
                        </div>
                        <Link to="/insurance/policies" className="btn btn-outline-primary">
                            <i className="ti-arrow-left" style={{ marginRight: "6px" }}></i>
                            Back to Policies
                        </Link>
                    </div>

                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <form className="dc-dashboardboxcontent" style={{ padding: "30px" }} onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Policy Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="policy_number"
                                        value={formData.policy_number}
                                        onChange={handleChange}
                                        placeholder="Policy number"
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Customer <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        name="customer_id"
                                        value={formData.customer_id}
                                        onChange={handleSelectCustomer}
                                        disabled={saving || !customers.length}
                                    >
                                        <option value="">Select customer</option>
                                        {customers.map((customer) => (
                                            <option key={customer.id} value={customer.id}>
                                                {customer.name || customer.full_name || customer.display_name || `Customer #${customer.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Policy Holder Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="policy_holder_name"
                                        value={formData.policy_holder_name}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Coverage Type <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="coverage_type"
                                        list="coverage_options"
                                        value={formData.coverage_type}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                    {coverageSuggestions.length > 0 && (
                                        <datalist id="coverage_options">
                                            {coverageSuggestions.map((suggestion) => (
                                                <option key={suggestion} value={suggestion} />
                                            ))}
                                        </datalist>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Coverage Amount</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="coverage_amount"
                                        value={formData.coverage_amount}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Premium Amount <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="premium_amount"
                                        value={formData.premium_amount}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Payment Frequency</label>
                                    <select
                                        className="form-control"
                                        name="payment_frequency"
                                        value={formData.payment_frequency}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        {PAYMENT_FREQUENCIES.map((frequency) => (
                                            <option key={frequency.value} value={frequency.value}>
                                                {frequency.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Product</label>
                                    <select
                                        className="form-control"
                                        name="product_id"
                                        value={formData.product_id}
                                        onChange={handleChange}
                                        disabled={saving || !products.length}
                                    >
                                        <option value="">Select product</option>
                                        {products.map((product) => (
                                            <option key={product.id} value={product.id}>
                                                {product.name || `Product #${product.id}`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Nominee Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nominee_name"
                                        value={formData.nominee_name}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Nominee Relationship</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nominee_relationship"
                                        value={formData.nominee_relationship}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Start Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="start_date"
                                        value={formData.start_date}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="end_date"
                                        value={formData.end_date}
                                        onChange={handleChange}
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Status</label>
                                    <select
                                        className="form-control"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        disabled={saving}
                                    >
                                        {STATUS_OPTIONS.map((status) => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea
                                        className="form-control"
                                        name="notes"
                                        rows="4"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Add internal notes"
                                        disabled={saving}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ marginTop: "12px" }}>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() => navigate("/insurance/policies")}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? "Saving Changes..." : "Save Policy"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditPolicy;

